<script setup lang="ts">
    import {
        type CanEditCodeOptions,
        requireCanEditCode as vRequireCanEditCode
    } from '@/shared/directives/requirePermission';
    import {
        BatchDocument,
        createChangeDoc,
        createNewTrContentItem,
        ReleaseAutoSubmitResult,
        ResponsePromise,
        sqlQuery,
        TRDocumentContentLibrary
    } from '@/shared/helpers/api';
    import {
        ContentItem,
        RevisionAction,
        TRDocumentRecord,
        TRRevisionAction,
        TRRevisionRecord
    } from './TRDocumentQueries';
    import { computed, ref, watch } from 'vue';
    import { useTRDocumentStore } from './TRDocumentStore';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import CodeRefSelector from '@/shared/components/CodeRefSelector.vue';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import { useDialog } from 'buefy';
    import CodeEditSaveProgress from '@/shared/components/CodeEditSaveProgress.vue';

    const show = useShow();
    const dialog = useDialog();

    type Props = {
        document: TRDocumentRecord;
        revision: TRRevisionRecord;
        contentLibraryList: TRDocumentContentLibrary[];
    };

    const props = defineProps<Props>();

    const store = useTRDocumentStore();

    type EditContentItem = {
        Name: string;
        Description: string;
        IsValid: boolean;
        TRRevision: string;
        SourceItem: string;
        TRNotes: string;
        RevisionAction: RevisionAction;
        RevisionText: string;
        TrReference: string;
    };

    type CodeRefSourceItem = {
        identity: string;
        name: string;
        description: string;
    };

    type SourceItem = {
        Identity: string;
        Name: string;
        Description: string;
        IsValid: boolean;
        TrNotes: string;
        TrReference: string;
    };

    // All source items that is not used by any content item in the current revision - to be used in the CodeRefSelector
    const codeRefSourceItems = ref<CodeRefSourceItem[]>([]);
    // All source items that is not used by any content item in the current revision - to be used when creating a new content item
    const sourceItems = ref<SourceItem[]>([]);

    const selectedContentLibrary = ref<TRDocumentContentLibrary | undefined>();
    const selectedContentItem = ref<ContentItem | undefined>();
    const editItemClone = ref<EditContentItem | undefined>();
    const trRevisionActionItemFilter = ref<TRRevisionAction | null>(null);
    const isLoading = ref(false);

    watch(() => props.revision, () => {
        selectedContentItem.value = undefined;
        selectedContentLibrary.value = undefined;
        trRevisionActionItemFilter.value = null;
    });

    watch(selectedContentLibrary, async () => {
        selectedContentItem.value = undefined;
        if (selectedContentLibrary.value) {
            try {
                isLoading.value = true;
                await store.loadContentItems(
                    props.revision,
                    selectedContentLibrary.value);
            } catch (e) {
                show.showError('Failed to load content items. Refresh the page or try again later.');
            } finally {
                isLoading.value = false;
            }
        }
    });

    const selectedContentItemDescription = computed(() => selectedContentItem.value?.description ?? '');
    const selectedContentItemTRNotes = computed(() => selectedContentItem.value?.trNotes ?? '');
    const selectedContentItemRevisionText = computed(() => selectedContentItem.value?.revisionText ?? '');
    const editContentItemPermissionSpecification = computed<CanEditCodeOptions | undefined>(() => {
        const libraryName = selectedContentLibrary.value?.name;
        return libraryName ? { libraryName } : undefined;
    });

    watch(selectedContentItem, () => {
        if (selectedContentItem.value) {
            editItemClone.value = {
                Name: selectedContentItem.value.name,
                Description: selectedContentItemDescription.value,
                IsValid: true,
                TRRevision: selectedContentItem.value.trRevision,
                SourceItem: selectedContentItem.value.sourceItem,
                TRNotes: selectedContentItemTRNotes.value,
                RevisionAction: selectedContentItem.value.revisionAction,
                RevisionText: selectedContentItemRevisionText.value,
                TrReference: selectedContentItem.value.trReference
            };
        } else {
            editItemClone.value = undefined;
        }
    });

    const filteredContentItemList = computed(() => {
        const revisionActionFilter = trRevisionActionItemFilter.value;

        return revisionActionFilter === null
            ? store.contentItemList
            : store.contentItemList?.filter(x => x.revisionAction === revisionActionFilter.Name);
    });

    const isModified = computed(() => {
        return !(selectedContentItemDescription.value === editItemClone.value?.Description
            && selectedContentItemTRNotes.value === editItemClone.value?.TRNotes
            && selectedContentItem.value?.revisionAction === editItemClone.value?.RevisionAction
            && selectedContentItemRevisionText.value === editItemClone.value?.RevisionText);
    });

    function wasInPreviousRevision(item?: EditContentItem): boolean {
        if (!item) {
            return false;
        } else {
            return item.RevisionAction === 'Stay' || item.RevisionAction === 'Update';
        }
    }

    function rowCssClass(row: ContentItem) {
        switch (row.revisionAction) {
            case 'Add':
                return 'is-added';
            case 'Remove':
                return 'has-background-danger-light';
            case 'Update':
                return 'has-background-warning-light';
            default:
                return '';
        }
    }

    function rowIcon(row: ContentItem): string {
        switch (row.revisionAction) {
            case 'Add':
                return 'plus';
            case 'Remove':
                return 'close';
            case 'Stay':
                return 'check';
            case 'Update':
                return 'asterisk';
            default:
                return '';
        }
    }

    const missingReason = computed(() => {
        return !wasInPreviousRevision(editItemClone.value) && !editItemClone.value?.RevisionText;
    });

    const canSave = computed(() => {
        return isModified.value && !missingReason.value;
    });

    const isCreatingContentItem = ref<boolean>(false);
    const isCreatingSourceItem = ref<boolean>(false);

    function onCancelEdit(): void {
        editItemClone.value = undefined;
        selectedContentItem.value = undefined;
        isCreatingContentItem.value = false;
    }

    const isSaving = ref<boolean>(false);
    const releaseId = ref<number | null>(null);
    const saveProcessStarted = ref<boolean>(false);
    function onSave() {
        if (editItemClone.value) {
            isSaving.value = true;

            // Backend requires '{null}' as null signal to not be ignored
            // Make a copy of editItemClone to avoid displaying '{null}' to the user while release is processed
            const contentItem = { ...editItemClone.value };

            if (!contentItem.TRNotes || contentItem.TRNotes.trim() === '') {
                contentItem.TRNotes = '{null}';
            }
            if (!contentItem.RevisionText || contentItem.RevisionText.trim() === '') {
                contentItem.RevisionText = '{null}';
            }

            // If the action is 'Stay' but have changes, we change it to 'Update' for filtering and visualization purposes
            if (contentItem.RevisionAction === 'Stay') {
                contentItem.RevisionAction = 'Update';
            }

            createChangeDoc(http, selectedContentLibrary.value!.codeSetName, [contentItem])
                .then(async (response) => {
                    releaseId.value = response.data.releaseId;
                    saveProcessStarted.value = true;
                })
                .catch((e) => show.showError('Failed to save changes: ' + e.message))
                .finally(() => {
                    isSaving.value = false;
                });
        }
    }

    async function loadContentItemsAndSetSelected(sourceItemIdentity: string | null) {
        await store.loadContentItems(
            props.revision,
            selectedContentLibrary.value!);
        selectedContentItem.value = store.contentItemList?.find(x => x.sourceItem === sourceItemIdentity);
    }

    async function onSaveContentItemFinished(): Promise<void> {
        saveProcessStarted.value = false;
        releaseId.value = null;

        const identity = selectedContentItem.value?.sourceItem ?? editItemClone.value?.SourceItem;
        await loadContentItemsAndSetSelected(identity ?? null);
        show.showInfo('Changes commited!');
        isCreatingContentItem.value = false;
    }

    async function onCreateContentItem(): Promise<void> {
        await loadSourceItems();
        editItemClone.value = {
            Name: '',
            Description: '',
            IsValid: true,
            SourceItem: '',
            TRNotes: '',
            RevisionText: '',
            TRRevision: props.revision.identity,
            RevisionAction: 'Add',
            TrReference: ''
        };
        isCreatingContentItem.value = true;
    }

    type NewSourceItemInfo = {
        sourceCodeIdentity: string;
        contentCodeIdentity: string;
    };

    const newSourceItemInfo = ref<NewSourceItemInfo | null>(null);

    async function onSourceItemCreated(): Promise<void> {
        await loadSourceItems();
        await loadContentItemsAndSetSelected(newSourceItemInfo.value?.sourceCodeIdentity ?? null);
        newSourceItemInfo.value = null;
        isCreatingSourceItem.value = false;
        isCreatingContentItem.value = false;
    }

    async function onCreateSourceItem(): Promise<void> {
        if (selectedContentLibrary.value) {
            isCreatingSourceItem.value = true;
        }
    }

    function onSourceItemCancelled(): void {
        isCreatingSourceItem.value = false;
    }

    async function loadSourceItems(): Promise<void> {
        const resp = await sqlQuery(http, {
            query: `
            SELECT
                [identity],
                [name],
                [description],
                ${selectedContentLibrary.value?.trReferenceAttributeName} as trReference,
                ${selectedContentLibrary.value?.trNotesAttributeName} as trNotes,
                isValid
            FROM ${selectedContentLibrary.value?.contentForLibrary}`
        });

        const usedSourceItemIdentities = store.contentItemList?.map(x => x.sourceItem) ?? [];

        const filtered = resp.data?.filter((x: any) => !usedSourceItemIdentities.includes(x.identity)) ?? [];

        codeRefSourceItems.value = filtered
            .map((x: any) => ({
                identity: x.identity,
                name: x.identity, // To facilitate CodeRefSelector UI: use 'identity' as name
                description: x.trReference // To facilitate CodeRefSelector UI: use 'trReference' as description
            } as CodeRefSourceItem))
            .sort((a, b) => a.name.localeCompare(b.name));

        sourceItems.value = filtered
            .map((x: any) => ({
                Identity: x.identity,
                Name: x.name,
                Description: x.description,
                IsValid: x.isValid,
                TrReference: x.trReference,
                TrNotes: x.trNotes
            } as SourceItem));
    }

    function sourceItemChanged(selected: string | null): void {
        if (editItemClone.value) {
            const sourceItem = sourceItems.value.find(x => x.Identity === selected);
            editItemClone.value.SourceItem = selected ?? '';
            editItemClone.value.Name = sourceItem?.Name ?? '';
            editItemClone.value.Description = sourceItem?.Description ?? '';
            editItemClone.value.TRNotes = sourceItem?.TrNotes ?? '';
            editItemClone.value.TrReference = sourceItem?.TrReference ?? '';
        }
    }

    const newCodeLabel = computed(() => {
        return 'Add new ' + (selectedContentLibrary.value?.contentForLibrary ?? 'item');
    });

    /**
     * Undo remove should only be used on content items that was part of the last revision (RevisionAction: { Stay, Update})
     * Content items that was added in this revision should be marked with IsValid = false
     */
    function onUndoRemove(): void {
        if (editItemClone.value) {
            editItemClone.value.RevisionText = '';
            // Note: Could possibly be marked as 'Stay':
            // if there are no changes to the item, compared to either sourceItem or contentItem in previous revision
            // but was deemed as not worth the cost.
            editItemClone.value.RevisionAction = 'Update';
        }
    }

    function onRemoveAddedItem(): void {
        dialog.confirm({
            message: 'This will remove the content item from the revision. It will no longer be visible or accessible from this UI',
            onConfirm: () => {
                if (editItemClone.value) {
                    editItemClone.value.IsValid = false;
                    editItemClone.value.RevisionText = '';
                    onSave();
                }
            }
        });
    }

    async function getReasonToAdd() {
        let reasonProvidedCallback: (_: string) => void;

        const reasonProvidedPromise: Promise<string> = new Promise((resolve) => {
            reasonProvidedCallback = resolve;
        });

        dialog.prompt({
            message: 'Please provide a reason for adding this item:',
            inputAttrs: {
                minlength: 1,
                maxlength: 500,
                placeholder: 'Reason for adding this item',
                required: true
            },
            trapFocus: true,
            onConfirm: (reason: string) => reasonProvidedCallback(reason.trim()),
            onCancel: () => reasonProvidedCallback('')
        });
        return await reasonProvidedPromise;
    }

    async function customCommitCallback(sourceCodeSet: BatchDocument): ResponsePromise<ReleaseAutoSubmitResult> {
        const reasonToAdd = await getReasonToAdd();

        if (!reasonToAdd) {
            return Promise.reject(new Error('A reason for adding the item is required.'));
        }

        try {
            const response = await createNewTrContentItem(http, props.revision.identity, {
                reasonToAdd: reasonToAdd,
                codeSetName: sourceCodeSet.codeSetName,
                code: sourceCodeSet.codes[0]
            });

            newSourceItemInfo.value = {
                sourceCodeIdentity: response.data.sourceCodeIdentity,
                contentCodeIdentity: response.data.contentCodeIdentity
            };

            return {
                ... response,
                data: {
                    releaseId: response.data.releaseId,
                    message: selectedContentLibrary.value
                        ? `Release chain created to add new code for source (${selectedContentLibrary.value.contentForLibrary}) and content library (${selectedContentLibrary.value.name})`
                        : 'Release chain created to add new code.'
                }
            };
        } catch (error) {
            return Promise.reject(error);
        }
    }

</script>

<template>
    <div
        v-if="revision"
        class="panel">
        <b-field grouped>
            <b-field
                label="Content library"
                expanded>
                <b-select
                    v-model="selectedContentLibrary"
                    placeholder="Select content library"
                    expanded>
                    <option
                        v-for="lib in contentLibraryList"
                        :value="lib">
                        {{ lib.name }}
                    </option>
                </b-select>
            </b-field>
        </b-field>

        <section
            v-if="selectedContentLibrary"
            class="buttons is-justify-content-right mb-3">
            <b-button
                v-if="!isCreatingContentItem && !revision.isAllFinished"
                v-require-can-edit-code="editContentItemPermissionSpecification"
                icon-left="plus-circle"
                type="is-primary"
                @click="onCreateContentItem">
                Add content item
            </b-button>
        </section>

        <!--Content Item List-->
        <section v-if="selectedContentLibrary && !isCreatingContentItem">
            <b-table
                v-model:selected="selectedContentItem"
                :data="filteredContentItemList"
                :loading="store.initializing || isLoading"
                :per-page="10"
                paginated
                focusable
                :row-class="rowCssClass">
                <b-table-column
                    v-slot="tableProps"
                    field="sourceItem"
                    :label="selectedContentLibrary.contentForLibrary"
                    sortable
                    searchable>
                    {{ tableProps.row.sourceItem }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="trReference"
                    label="TR Reference"
                    sortable
                    searchable>
                    {{ tableProps.row.trReference }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="description"
                    label="Description"
                    sortable
                    searchable>
                    {{ tableProps.row.description }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="trNotes"
                    label="Notes"
                    sortable
                    searchable>
                    {{ tableProps.row.trNotes }}
                </b-table-column>
                <b-table-column
                    sortable
                    searchable
                    field="revisionAction"
                    label="RevAction"
                    width="100">
                    <template #searchable>
                        <b-select
                            v-model="trRevisionActionItemFilter"
                            expanded>
                            <option :value="null">
                                All
                            </option>
                            <option
                                v-for="action in store.revisionActionOptions"
                                :value="action">
                                {{ action.Description }}
                            </option>
                        </b-select>
                    </template>
                    <template
                        #default="tableProps">
                        <b-icon
                            :icon="rowIcon(tableProps.row)"
                            size="is-small" />
                        {{ tableProps.row.revisionAction }}
                    </template>
                </b-table-column>
            </b-table>
        </section>

        <!-- Edit Content Item / Create Content Item-->
        <section v-if="selectedContentLibrary && editItemClone">
            <b-field
                grouped
                disabled="!revision.isAllFinished">
                <b-field
                    v-if="isCreatingContentItem"
                    :label="selectedContentLibrary.contentForLibrary"
                    expanded>
                    <b-button
                        v-if="isCreatingContentItem"
                        icon-left="plus-circle"
                        type="is-primary"
                        class="mr-3"
                        @click="onCreateSourceItem">
                        Add new
                    </b-button>
                    <div class="is-full-width">
                        <code-ref-selector
                            :selected="editItemClone.SourceItem"
                            :code-refs="codeRefSourceItems"
                            :complex="false"
                            @ref-selected="(selected: string | null) => sourceItemChanged(selected)" />
                    </div>
                </b-field>
                <b-field
                    v-else
                    :label="selectedContentLibrary.contentForLibrary"
                    expanded>
                    <b-input
                        v-model="editItemClone.SourceItem"
                        disabled />
                </b-field>
                <b-field
                    label="TR Reference"
                    expanded>
                    <b-input
                        v-model="editItemClone.TrReference"
                        disabled />
                </b-field>
            </b-field>

            <b-field
                grouped>
                <b-field
                    label="Description"
                    expanded>
                    <b-input
                        v-model="editItemClone.Description"
                        :disabled="revision.isAllFinished" />
                </b-field>
                <b-field
                    label="Notes"
                    expanded>
                    <b-input
                        v-model="editItemClone.TRNotes"
                        :disabled="revision.isAllFinished" />
                </b-field>
            </b-field>

            <b-field grouped>
                <b-field
                    label="Action">
                    <b-input
                        disabled
                        :model-value="editItemClone.RevisionAction" />
                </b-field>

                <b-field
                    label="Reason for adding or removing this item"
                    :type="missingReason ? 'is-danger' : ''"
                    :message="missingReason ? 'This change requires a reason' : ''"
                    expanded>
                    <b-input
                        v-model="editItemClone.RevisionText"
                        :disabled="revision.isAllFinished || wasInPreviousRevision(editItemClone)" />
                </b-field>
            </b-field>
            <div
                v-if="!revision.isAllFinished"
                class="level">
                <div
                    class="buttons level-left mb-0">
                    <b-button
                        v-if="editItemClone.RevisionAction === 'Add' && !isCreatingContentItem"
                        v-require-can-edit-code="editContentItemPermissionSpecification"
                        type="is-danger"
                        icon-left="delete"
                        @click="onRemoveAddedItem">
                        Remove
                    </b-button>
                    <b-button
                        v-else-if="editItemClone.RevisionAction === 'Remove'"
                        v-require-can-edit-code="editContentItemPermissionSpecification"
                        type="is-info"
                        icon-left="replay"
                        @click="onUndoRemove">
                        Undo remove
                    </b-button>
                    <b-button
                        v-else-if="wasInPreviousRevision(editItemClone)"
                        v-require-can-edit-code="editContentItemPermissionSpecification"
                        type="is-info"
                        icon-left="delete"
                        @click="editItemClone.RevisionAction = 'Remove'">
                        Remove
                    </b-button>
                </div>
                <div class="buttons level-right">
                    <b-button
                        type="is-default"
                        @click="onCancelEdit">
                        Cancel
                    </b-button>
                    <b-button
                        v-if="!revision.isAllFinished"
                        v-require-can-edit-code="editContentItemPermissionSpecification"
                        :disabled="!canSave"
                        :loading="isSaving"
                        type="is-primary"
                        icon-left="floppy"
                        @click="onSave">
                        Save
                    </b-button>
                </div>
            </div>
        </section>
        <b-modal
            v-model="isCreatingSourceItem">
            <code-edit
                style="background-color: #fff;padding: 20px;"
                :library="selectedContentLibrary?.contentForLibrary ?? ''"
                :code-set-name="selectedContentLibrary?.contentForCodeSet"
                :code-template-values="{ 'isValid': 'false'}"
                :fields-to-disable="['isValid']"
                :fields-to-ignore="['isValid']"
                :form-title="newCodeLabel"
                :custom-commit="customCommitCallback"
                @refresh="onSourceItemCreated"
                @cancel="onSourceItemCancelled" />
        </b-modal>
        <code-edit-save-progress
            :save-process-started="saveProcessStarted"
            :release-id="releaseId"
            :close-enabled="false"
            @success="onSaveContentItemFinished" />
    </div>
</template>
<style scoped>
::v-deep(.is-added) {
    background-color: var(--eq-norwegian-woods-6) !important;

}
</style>
